/**
 * EventBus Schema - Infrastructure Types
 * Centralized types for event bus implementations (SocketIO, Redis, etc.)
 * Following Schema-First Development pattern
 */

import { z } from 'zod';

// Base EventBus Configuration Schema
export const EventBusConfigSchema = z.object({
  environment: z.enum(['development', 'staging', 'production']),
  connectionTimeout: z.number().optional(),
  heartbeatInterval: z.number().optional(),
  maxConnections: z.number().optional(),
});

// SocketIO Specific Configuration Schema
export const SocketIOEventBusConfigSchema = EventBusConfigSchema.extend({
  server: z.any().optional(), // HTTP server instance
  port: z.number().optional(),
  path: z.string().optional(),
  namespace: z.string().optional(), // Socket.IO namespace
  cors: z.any().optional(), // CORS configuration
  connectionTimeout: z.number().optional(),
  heartbeatInterval: z.number().optional(),
  maxConnections: z.number().optional(),
  adapter: z.enum(['memory', 'redis']).optional(),
  redis: z.object({
    host: z.string(),
    port: z.number(),
    password: z.string().optional(),
  }).optional(),
});

// EventBus Environment Schema
export const EventBusEnvironmentSchema = z.object({
  server: z.any().optional(),
  port: z.number().optional(),
  environment: z.enum(['development', 'staging', 'production']),
  cors: z.any().optional(),
  redis: z.object({
    host: z.string(),
    port: z.number(),
    password: z.string().optional(),
  }).optional(),
});

// User Presence Schema
export const UserPresenceSchema = z.object({
  userId: z.string(),
  username: z.string().optional(),
  status: z.enum(['online', 'idle', 'away', 'busy', 'offline']),
  lastSeen: z.date(),
  activity: z.string().optional(),
  metadata: z.record(z.string(), z.any()).optional(),
});

// TypeScript Types (inferred from schemas)
export type EventBusConfig = z.infer<typeof EventBusConfigSchema>;
export type SocketIOEventBusConfig = z.infer<typeof SocketIOEventBusConfigSchema>;
export type EventBusEnvironment = z.infer<typeof EventBusEnvironmentSchema>;
export type UserPresence = z.infer<typeof UserPresenceSchema>;